import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const GROUPS = [
  ["Workspace", ["dashboard", "leads", "site_visits", "follow_ups", "whatsapp", "callerdesk"]],
  ["Project", ["projects", "inventory"]],
  ["My Console", ["reports", "lead_sources", "data_import", "wa_templates", "message_templates", "bulk_allocation", "partners", "proposals", "team", "settings"]],
];

export const usePermissions = (enabled = true) => {
  const [data, setData] = useState(null);
  const load = () => api.get("/permissions").then((r) => setData(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (enabled) load(); }, [enabled]);
  return [data, load];
};

const label = (data, k) => data.sections.find((s) => s.key === k)?.label || k;

export const RolePermissions = ({ data, onSaved }) => {
  const [roles, setRoles] = useState(data.roles);
  const [busy, setBusy] = useState(false);
  useEffect(() => setRoles(data.roles), [data]);
  const flip = (r, k, v) => setRoles((p) => ({ ...p, [r]: { ...p[r], [k]: v } }));
  const save = async () => {
    setBusy(true);
    try { await api.put("/permissions/roles", roles); toast.success("Role permissions saved"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div data-testid="role-permissions" className="border border-[#E6E4DD] bg-white rounded-sm">
      <div className="px-4 sm:px-5 py-4 border-b border-[#E6E4DD] flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="label-caps">Permissions by role</div>
          <div className="text-xs text-forest/60 mt-1">Admins always see everything. Turning a section off hides it and blocks its pages and actions.</div>
        </div>
        <button data-testid="role-permissions-save" onClick={save} disabled={busy}
          className="h-9 px-4 rounded-sm bg-forest text-white text-sm font-medium hover:bg-forest-soft transition-colors duration-150 disabled:opacity-60">Save role permissions</button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-8 gap-y-5 p-4 sm:p-5">
        {GROUPS.map(([g, keys]) => (
          <div key={g} className="min-w-0">
            <div className="grid grid-cols-[minmax(0,1fr)_76px_76px] gap-x-2 items-center text-[10px] uppercase tracking-[0.15em] font-bold text-forest/50 pb-2 border-b border-[#E6E4DD]">
              <span>{g}</span><span className="text-center">Manager</span><span className="text-center">Executive</span>
            </div>
            {keys.map((k) => (
              <div key={k} className="grid grid-cols-[minmax(0,1fr)_76px_76px] gap-x-2 items-center py-2 border-b border-[#E6E4DD]/60">
                <span className="text-sm text-forest truncate">{label(data, k)}</span>
                {["manager", "executive"].map((r) => (
                  <span key={r} className="flex justify-center">
                    <Switch data-testid={`perm-${r}-${k}`} checked={!!roles[r]?.[k]} onCheckedChange={(v) => flip(r, k, v)} />
                  </span>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const OPTIONS = [["default", "Role default"], ["on", "On"], ["off", "Off"]];

export const MemberPermissionsDialog = ({ member, data, onClose, onSaved }) => {
  const [over, setOver] = useState({});
  useEffect(() => { if (member) setOver(data.overrides[member.id] || {}); }, [member, data]);
  if (!member) return null;
  const base = data.roles[member.role] || {};
  const save = async () => {
    try { await api.put(`/users/${member.id}/permissions`, { overrides: over }); toast.success(`Access updated for ${member.name}`); onSaved(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const set = (k, v) => setOver((p) => { const n = { ...p }; if (v === "default") delete n[k]; else n[k] = v === "on"; return n; });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="member-permissions-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Access for {member.name} <span className="text-forest/50 text-sm font-normal">· {member.role}</span></DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
          {data.sections.map(({ key: k, label: l }) => {
            const v = k in over ? (over[k] ? "on" : "off") : "default";
            const eff = k in over ? over[k] : !!base[k];
            return (
              <div key={k} className="flex items-center justify-between gap-2 py-2 border-b border-[#E6E4DD]/60 min-w-0">
                <span className="text-sm text-forest truncate">{l} <span className={`text-[10px] ml-1 ${eff ? "text-[#2D6A4F]" : "text-clay"}`}>{eff ? "on" : "off"}</span></span>
                <select data-testid={`member-perm-${k}`} value={v} onChange={(e) => set(k, e.target.value)} className="h-8 border border-[#E6E4DD] rounded-sm px-1.5 text-xs bg-white">
                  {OPTIONS.map(([o, ol]) => <option key={o} value={o}>{ol}</option>)}
                </select>
              </div>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="h-9 px-4 rounded-sm border border-[#E6E4DD] text-sm">Cancel</button>
          <button data-testid="member-permissions-save" onClick={save} className="h-9 px-4 rounded-sm bg-forest text-white text-sm font-medium hover:bg-forest-soft">Save access</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const PermissionsButton = ({ onClick, id }) => (
  <button data-testid={`team-permissions-${id}`} onClick={onClick} title="Section access" className="text-forest/50 hover:text-forest transition-colors duration-150 p-1.5 rounded-sm">
    <ShieldCheck className="h-3.5 w-3.5" />
  </button>
);
